import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useCapture } from "@/context/CaptureContext";
import { PageHeader, EmptyState, CaptureThumb } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Plus } from "@/lib/icons";

const TYPES = ["note", "photo", "audio", "video", "file", "link"];

export default function Catalog() {
  const { openCapture } = useCapture();
  const [caps, setCaps] = useState([]);
  const [collections, setCollections] = useState([]);
  const [tags, setTags] = useState([]);
  const [type, setType] = useState("all");
  const [tag, setTag] = useState("all");
  const [collectionId, setCollectionId] = useState("all");
  const [sort, setSort] = useState("recent");

  const load = () => {
    const params = {};
    if (type !== "all") params.type = type;
    if (tag !== "all") params.tag = tag;
    if (collectionId !== "all") params.collection_id = collectionId;
    params.sort = sort;
    api.get("/captures", { params }).then((r) => setCaps(r.data.captures)).catch(() => {});
  };
  useEffect(() => { load(); }, [type, tag, collectionId, sort]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener("dl-saved", h);
    return () => window.removeEventListener("dl-saved", h);
  }, []);
  useEffect(() => {
    api.get("/collections").then((r) => setCollections(r.data.collections)).catch(() => {});
    api.get("/tags").then((r) => setTags(r.data.tags)).catch(() => {});
  }, []);

  const active = type !== "all" || tag !== "all" || collectionId !== "all";
  const clear = () => { setType("all"); setTag("all"); setCollectionId("all"); setSort("recent"); };

  return (
    <div className="max-w-6xl mx-auto">
      <PageHeader eyebrow="Catalog" color="#3B82F6" title="Catalog"
        subtitle="Organize it. Find it. Make it yours."
        right={<Button onClick={() => openCapture()} className="gap-2" style={{ background: "#2563EB" }}><Plus size={16} /> Capture</Button>} />

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <FilterSelect value={type} onChange={setType} testid="filter-type" placeholder="All types" options={[{ v: "all", l: "All types" }, ...TYPES.map((t) => ({ v: t, l: t }))]} />
        <FilterSelect value={collectionId} onChange={setCollectionId} testid="filter-collection" options={[{ v: "all", l: "All collections" }, ...collections.map((c) => ({ v: c.id, l: c.name }))]} />
        <FilterSelect value={tag} onChange={setTag} testid="filter-tag" options={[{ v: "all", l: "All tags" }, ...tags.map((t) => ({ v: t.name, l: `#${t.name}` }))]} />
        <FilterSelect value={sort} onChange={setSort} testid="filter-sort" options={[{ v: "recent", l: "Newest" }, { v: "oldest", l: "Oldest" }, { v: "title", l: "Title A–Z" }, { v: "captured", l: "Capture date" }]} />
        {active && <Button variant="ghost" size="sm" onClick={clear} data-testid="clear-filters-btn" className="text-slate-400">Clear filters</Button>}
      </div>

      {caps.length === 0 ? (
        <EmptyState icon={Search} title="Nothing here yet"
          hint={active ? "No captures match these filters. Try clearing them." : "Your catalog is empty. Capture something to get started."}
          action={<Button onClick={() => openCapture()} style={{ background: "#2563EB" }}>Capture now</Button>} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="catalog-grid">
          {caps.map((c) => <CaptureThumb key={c.id} cap={c} />)}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ value, onChange, options, testid, placeholder }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="w-auto min-w-[140px] bg-black/20 capitalize"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.v} value={o.v} className="capitalize">{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
